<template>
  <LayoutLegal title="Terms for Users">
    <template #sidebar>
      <ul class="flex flex-col gap-6 leading-snug">
        <li><a href="#1">Enrollment</a></li>
        <li><a href="#2">Privacy</a></li>
        <li><a href="#3">User Account</a></li>
        <li><a href="#4">Content Purchases</a></li>
        <li><a href="#5">Statements of Fact</a></li>
        <li><a href="#6">Termination/Suspension</a></li>
      </ul>
    </template>

    <h2>Terms for Users</h2>

    <p>
      If you have registered as a User at our Platform, this Terms for Users
      will apply in addition to the General Terms of Service. If there is a
      conflict between the General Terms and this Terms for Users, the terms of
      the latter shall prevail. Terms not otherwise defined in this document
      shall have the same meaning assigned to them in the General Terms of
      Service.
    </p>
    <p>
      You must have first agreed to the General Terms of Service in order to
      accept this Terms for Users. By clicking on the words "I agree," "Submit,"
      or similar syntax, you are electronically signing this agreement, and
      therefore agree to be bound by and acknowledge your complete acceptance of
      all the express and incorporated terms of this Terms for Users. If you do
      not agree to this Terms for Users or do not meet the age requirements, you
      must not register.
    </p>
    <p>
      The parties therefore agree to the following terms in addition to those
      agreed to in the General Terms of Service:
    </p>
    <h3 id="1">ENROLLMENT</h3>
    <p>
      To be eligible to participate in the Platform as a User, you must be an
      individual who is at least 18-years old and has the legal capacity to
      enter into legally binding contracts.
    </p>
    <p>
      By registering, you state that all account registration information is
      accurate.
    </p>
    <h3 id="2">PRIVACY</h3>
    <p>
      For information about how Favoritely LLC ("FLLC") collects, uses, and
      shares the User's personal information, please review the Platform's
      Privacy Policy. The User acknowledges that FLLC may retain indefinitely
      information the User submits to FLLC, including the User's identification,
      in case the information is needed to comply with applicable law or in a
      good-faith belief that preservation or disclosure of that information is
      reasonably necessary in the FLLC's opinion to (a) comply with legal
      process, including civil and criminal subpoenas, court orders, or other
      compulsory disclosure; (b) enforce this agreement; (c) respond to claims
      of a violation of the rights of third parties, regardless of whether the
      third party is a user, individual, or government agency; or (d) protect
      the rights, property, or personal safety of FLLC, the Platform's users, or
      the public.
    </p>
    <h3 id="3">USER ACCOUNT</h3>
    <p>
      You must register where indicated in the Platform to create an Account as
      User. You are responsible for ensuring that the information provided for
      purposes of receiving payment is kept up to date at all times.
    </p>
    <p>
      If you desire to purchase access to or Content from Creators posted
      Creator's Offerings, you must follow the process as prompted in the
      Platform and described in Section 4, below.
    </p>
    <p>
      WE MAY, IN OUR SOLE DISCRETION, LIMIT, SUSPEND, TERMINATE, MODIFY, OR
      DELETE ACCOUNTS OR ACCESS TO THE PLATFORM OR ANY PORTION OF IT; PROHIBIT
      ACCESS TO OUR PLATFORM, AND ITS CONTENT, AND TOOLS; OR DELAY OR REMOVE
      HOSTED CONTENT, AND WE UNDER NO OBLIGATION ARE REQUIRED TO COMPENSATE YOU
      FOR ANY LOSSES OR RESULTS. YOU MAY NOT BRING ANY CLAIM AGAINST FLLC OR ITS
      AFFILIATE ENTITIES FOR LIMITING ACCESS TO OUR PLATFORM, SUSPENSION,
      TERMINATION, OR DELETION OF ANY ACCOUNT FOR ANY REASON. IF ANY SUCH CLAIM
      IS BROUGHT AGAINST US, YOU ARE RESPONSIBLE FOR ALL DAMAGES CAUSED TO FLLC,
      INCLUDING ATTORNEY FEES AND COSTS. THESE TERMS REMAIN IN EFFECT IF THE
      ACCOUNT IS ACTIVE OR INACTIVE OR YOU NO LONGER HAVE AN ACCOUNT.
    </p>
    <h3 id="4">CONTENT PURCHASES</h3>
    <p>
      <span class="underline">In General</span>
      . The Platform allows Creators to offer and sell access to the User they
      post in the Platform and Users to purchase access to such Creator's
      Offering in exchange for a one-time or recurring fee, as determined by
      each Creator. Creators set their own pricing. Pricing excludes any taxes
      or currency transmission charges, which are extra costs charged to you.
      Buying paid services from one Creator does not grant you access to Content
      from another Creator. You must separately buy Content to each Creator you
      want access to. All relevant rules for payments for Content Purchases are
      set forth in this
      <a href="/terms-for-creators">Terms for Creators</a>
      .
    </p>
    <p>
      <span class="underline">Accounts</span>
      . The Platform allows Users to buy tokens from us to pay for Content
      offered by Creators registered with the Platform. Users will not be
      allowed to buy and store in their Accounts more than $2,000 worth of
      tokens in any given day. Any tokens purchased by Users are subject to
      expire if not used within twelve months after the date of purchase. Tokens
      have no cash value and may be used exclusively to pay for Content
      Purchases under the Platform. Users are not allowed to sell or transfer
      the Tokens under a given Account in any way to any other third party.
    </p>
    <p>
      <span class="underline">Payments</span>
      . Payments are made through our authorized payment processor. The Platform
      accepts payments via the payment methods identified in our website before
      checkout. You must have a valid accepted form of payment on file to
      purchase tokens or participate in free trial offers or other free offers
      related to any Creator's Offering. You must abide by any relevant terms of
      service or any other legal agreement, whether with us or a third party,
      that governs your use of a given payment method. Prices for gaining access
      to any Creator's Offering may change at any time, and we do not provide
      price protection or refunds in the event of a price reduction or
      promotional offering. It is your responsibility to check the price before
      making a purchase. You must prepay for any Creator's Offering that you
      order. Our authorized payment processor will charge your credit card or
      other form of payment for the price listed for the applicable Creator's
      Offering, along with any additional amounts relating to applicable taxes,
      bank fees, and currency fluctuations.
    </p>
    <p>
      <span class="underline">Recurring Billing</span>
      . If you purchase an automatically renewing subscription, you authorize us
      to deduct from your Account all tokens necessary to pay for the
      subscription on the first day of each billing period. In case your Account
      has not enough tokens to make the payment for the recurring subscription,
      you authorize our payment processor to charge the payment method you have
      on file for enough tokens to complete the payment for the relevant
      subscription. If your Account is suspended or the payment method on file
      becomes invalid due to an expired credit card or other similar reason and
      our authorized payment processor is unable to charge you the next billing
      period, your access to any recurring paid service will be immediately
      revoked until you update your payment method or your Account is
      reinstated. If you fail to replenish your Account or to update your
      payment method within a reasonable amount of time, your subscription may
      be cancelled.
    </p>
    <p>
      <span class="underline">Taxes</span>
      . If we are required to collect or pay any taxes in connection with your
      purchase of tokens or Creator's Offering, those taxes will be charged to
      you at the time of each purchase transaction. Additionally, if required by
      law, you are responsible for reporting and paying certain taxes in
      connection with your purchase and use of a paid service. These taxes may
      include duties, customs fees, or other taxes (other than income tax),
      along with any related penalties or interest, as applicable to your
      purchase or country of purchase.
    </p>
    <p>
      <span class="underline">No Refunds</span>
      . All sales and transactions for Content Purchases are final. Payments are
      nonrefundable and there are no refunds or credits for partially used
      periods or paid for Content. But we may approve a refund in the form of a
      credit on request if exceptional circumstances exist. The amount and form
      of a refund, and the decision to provide it, is at our sole discretion.
      The provision of a refund in one instance does not entitle you to a refund
      in the future for similar instances; nor does it obligate us to provide
      refunds in the future, under any circumstance. With respect to tokens held
      in your User Account, you acknowledge that if you voluntarily close your
      Account before using or expiration of all your tokens, you will not
      receive a refund for your unused tokens, and forego any claims you may
      have for such unused tokens.
    </p>
    <p>
      <span class="underline">Subscription Cancellations</span>
      . If you buy a subscription to a Creator's Offering that automatically
      renews, you may cancel the subscription any time before the end of the
      current billing period and the cancellation will take effect on the next
      billing period. You will have access to the Creator's Offering from the
      time you cancel until the start of the next billing period; you will not
      receive a refund or credit for any remaining days in your current billing
      period. You can change the automatic renewal settings or cancel your
      subscription at any time by contacting the Company's authorized payment
      processor, who is identified in your purchase confirmation email. If you
      cannot find your confirmation email, please send the Company an email at
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      and it will provide you with the relevant information.
    </p>
    <p>
      <span class="underline">Billing Disputes</span>
      . If you believe that we have charged you in error, you must notify us in
      writing no later than 30 days after you receive the billing statement in
      which the error first appeared. If you fail to notify us in writing of a
      dispute within this 30-day period, you waive any disputed charges. You
      must submit any billing disputes by email to
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
      and include a detailed statement describing the nature and amount of the
      disputed charges. We will correct any mistakes in a bill and add or credit
      them against your future payments.
    </p>

    <h3 id="5">STATEMENTS OF FACT</h3>
    <p>
      The User states that the following facts are accurate and will continue to
      be accurate during his use of the Platform:
    </p>
    <p>
      Any Creator's Content posted through the Platform observes the
      requirements and limitations set forth in the Terms of Service, and such
      posted content will not:
    </p>
    <p>
      invade the right of privacy or publicity of any person (including invasion
      of rights of celebrity);
    </p>
    <p>
      involve any defamatory, libelous, slanderous, sexually explicit, obscene,
      indecent, scatological or otherwise unlawful material;
    </p>
    <p>violate any applicable law; or</p>
    <p>
      otherwise infringe on the rights of any third parties, including those of
      copyright, patent, trademark, service mark, trade name, trade secret, or
      other intellectual-property rights, or engage in false advertising, unfair
      competition, violation of antidiscrimination law, or violation of any
      other right of any person.
    </p>
    <p>
      Your Content will not display or depict any person under 18-years old at
      the time of production and you will not give access to the Content
      purchased through your Account in the Platform to any a person under 18
      years old or who has not reached the age of majority where they live.
    </p>

    <h3 id="6">TERMINATION/SUSPENSION</h3>
    <p>
      FLLC may, in its sole discretion, terminate or suspend your access to all
      or part of the Platform at any time, with or without notice, for any
      reason or no reason at all, including, without limitation, violation of
      any term of this Agreement. Any activity which we believe, in our sole
      discretion, to even possibly be illegal, fraudulent, abusive, or in
      violation of our community standards, or our rules, may be grounds for
      termination of your access to all or part of the Platform. In those cases,
      we reserve the right to refer any such activity to any appropriate law
      enforcement agencies. Termination of an Account will be paid for any
      tokens remaining in the Account at the time of termination, as long as the
      amount in the respective account exceeds payment minimums, paid according
      to payment information on file and on the next scheduled payout date. If
      the amount remaining in the account does not exceed payment minimums, the
      amount is forfeited.
    </p>
  </LayoutLegal>
</template>

<script setup>
  definePageMeta({
    skipAuth: true,
  })

  useHead({
    title: "Terms for Users - favoritely.com",
    meta: [{ name: "description", content: "favoritely.com" }],
  })
</script>
